import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import TagList from '@wix/da-ds/pkg/TagList';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import { SubmitFormContext } from '../../context';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import { useSubmitField } from '../../hooks/useSubmitField';
import { useFieldFormPopup } from '../../modals/FieldFormPopup';
import GroupSelectPopup from '../../modals/GroupSelectPopup';
import { groupDestinationsFetch } from '../../redux/actions';
import { getGroupDestinations } from '../../redux/selectors';
import { initAdapter, submitAdapter } from './adapters';

import s from './GroupSelectField.scss';

interface Props {
  className?: string;
}

const GroupSelectField: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();

  const { initResponse, params } = useContext(SubmitFormContext) || {};
  const deviationId = initResponse?.deviation?.deviationId;
  const dispatch = useDispatch();
  const groupDestinationsResponse = useSelector(getGroupDestinations);
  const [loaded, setLoaded] = useState<boolean>(false);

  const {
    id,
    field: { onChange, value: groups = [] },
  } = useSubmitField('groups', {
    submitAdapter,
  });

  const modalProps = useFieldFormPopup(groups, onChange);
  const selectedGroups = groups.filter(group => group.selected);
  useEffect(() => {
    if (
      !loaded &&
      deviationId &&
      groupDestinationsResponse.deviationId === deviationId &&
      groupDestinationsResponse.status === 'idle'
    ) {
      setLoaded(true);

      const newValue = initAdapter(
        groupDestinationsResponse.inGroups,
        groupDestinationsResponse.groupDestinations
      );
      yieldToMain(() => {
        modalProps.setValue(newValue);
        onChange(newValue);
      });
    }
  }, [
    loaded,
    groupDestinationsResponse,
    groupDestinationsResponse.deviationId,
    groupDestinationsResponse.status,
    params,
    deviationId,
    modalProps,
    modalProps.setValue,
    modalProps.onSave,
    onChange,
  ]);

  const toggleModal = useCallback(() => {
    modalProps.toggleModal();
    if (deviationId && groupDestinationsResponse.deviationId !== deviationId) {
      setLoaded(false);
      dispatch(groupDestinationsFetch(deviationId));
    }
  }, [modalProps, deviationId, groupDestinationsResponse.deviationId, dispatch]);

  const tagList = selectedGroups.length > 0 && (
    <TagList
      className={s['groups-selected']}
      tags={selectedGroups.map(group => {
        const selectedFolder = group.items.find(f => f.selected);
        return {
          tagname: `${group.group.username}, ${selectedFolder.label}`,
        };
      })}
      size="small"
      multiline
    />
  );

  return (
    <div className={className}>
      <ButtonFieldLayout
        label={t('deviationSubmit.form.groups.label')}
        labelProps={{ id: `${id}-label`, htmlFor: id }}
        bottomSlot={tagList}
      >
        <TextButton variant="main" size="small" onClick={toggleModal}>
          {selectedGroups.length > 0
            ? t('deviationSubmit.form.groups.button.edit')
            : t('deviationSubmit.form.groups.button.add')}
        </TextButton>
      </ButtonFieldLayout>

      {groupDestinationsResponse.status === 'fetching' && (
        <LoadingIndicator view="llama" withOverlay usePortal />
      )}
      {deviationId && loaded && <GroupSelectPopup {...modalProps} />}
    </div>
  );
};
GroupSelectField.displayName = 'GroupSelectField';

export default GroupSelectField;
